// src/App.tsx
import { Routes, Route, Navigate } from "react-router-dom";
import { Suspense, lazy } from "react";

// Providers (ya montados una sola vez en main.tsx: AppProvider > AuthProvider)
import { useAuth } from "./context/AuthProvider";

import ProtectedRoute from "./routes/ProtectedRoute";
import AdminRoute from "./routes/AdminRoute";

// Layouts
import AppLayout from "./layouts/AppLayout";
import AdminLayout from "./layouts/AdminLayout";

// Loader Lottie
import FullScreenLoader from "./components/ui/FullScreenLoader";

// Páginas (lazy)
const Login = lazy(() => import("./pages/Login"));
const AuthAction = lazy(() => import("./pages/AuthAction"));

// App (usuario)
const AppHome = lazy(() => import("./pages/App/Home"));
const AppClassesList = lazy(() => import("./pages/App/Classes/List"));
const ClassesDetail = lazy(() => import("./pages/App/Classes/Detail"));
const Profile = lazy(() => import("./pages/App/Profile"));
const MyBookings = lazy(() => import("./pages/App/Bookings/List"));
// const AppDashboard = lazy(() => import("./pages/App/Dashboard/index")); // No usado actualmente
const NotificationsListApp = lazy(() => import("./pages/App/Notifications/List"));
const AuthDebug = lazy(() => import("./pages/tools/AuthDebug"));
// Admin
const AdminDashboard = lazy(() => import("./pages/Admin/Dashboard"));
const AdminClassesList = lazy(() => import("./pages/Admin/Classes/AdminList"));
const NewSession = lazy(() => import("./pages/Admin/Classes/NewSession"));
const EditSession = lazy(() => import("./pages/Admin/Classes/EditSession"));
const BulkSchedule = lazy(() => import("./pages/Admin/Classes/Bulk/BulkSchedule"));
const SessionDetail = lazy(() => import("./pages/Admin/Classes/SessionDetail"));
const SessionAttendance = lazy(() => import("./pages/Admin/Classes/SessionAttendance"));
const ClassTypesList = lazy(() => import("./pages/Admin/Classes/ClassTypes/List"));
const ClassTypeNew = lazy(() => import("./pages/Admin/Classes/ClassTypes/NewType"));
const ClassTypeEdit = lazy(() => import("./pages/Admin/Classes/ClassTypes/EditType"));
const ReportsHome = lazy(() => import("./pages/Admin/Reports/Home"));
const ReportsOccupancy = lazy(() => import("./pages/Admin/Reports/Occupancy"));
const ReportsCheckins = lazy(() => import("./pages/Admin/Reports/Checkins"));
const PlansIndex = lazy(() => import("./pages/Admin/Plans/Index"));
const PlanEdit = lazy(() => import("./pages/Admin/Plans/Edit"));
const NotificationsList = lazy(() => import("./pages/Admin/Notifications/List"));

// NUEVO: Panel para crear recordatorios/avisos (admin)
const ReminderPanel = lazy(() => import("./pages/Admin/Notifications/ReminderPanel"));

// Usuarios (ADMIN)
const UsersIndex = lazy(() => import("./pages/Admin/Users/Index"));
const UserDetail = lazy(() => import("./pages/Admin/Users/UserDetail"));
const AssignRole = lazy(() => import("./pages/Admin/Users/AssignRole"));
const BulkBookForUser = lazy(() => import("./pages/Admin/Users/BulkBookForUser"));

// Cronómetro
const IntervalTimer = lazy(() => import("./pages/Admin/IntervalTimer"));

// Rutinas (Admin)
const ExercisesList = lazy(() => import("./pages/Admin/Routines/ExercisesList"));
const ExerciseForm = lazy(() => import("./pages/Admin/Routines/ExerciseForm"));
const RoutinesList = lazy(() => import("./pages/Admin/Routines/RoutinesList"));
const RoutineForm = lazy(() => import("./pages/Admin/Routines/RoutineForm"));
const AssignRoutine = lazy(() => import("./pages/Admin/Routines/AssignRoutine"));
const TrainerReport = lazy(() => import("./pages/Admin/Routines/TrainerReport"));

// Rutinas (Member)
const MyRoutines = lazy(() => import("./pages/App/Routines/MyRoutines"));
const RoutineDetail = lazy(() => import("./pages/App/Routines/RoutineDetail"));

// Achievements
const AchievementsPage = lazy(() => import("./pages/App/Achievements/AchievementsPage"));

// PWA prompts
const InstallPrompt = lazy(() => import("./components/pwa/InstallPrompt"));
const UpdatePrompt = lazy(() => import("./components/pwa/UpdatePrompt"));
const IosInstallHint = lazy(() => import("./components/pwa/IosInstallHint"));
const OfflineBanner = lazy(() => import("./components/pwa/OfflineBanner"));

// 🔹 NUEVO: ruta pública para limpiar SW/caches cuando haga falta
const PwaReset = lazy(() => import("./pages/tools/PwaReset"));

export default function App() {
  return (
    <Suspense fallback={<FullScreenLoader message="Preparando tu entrenamiento…" solidBackdrop />}>
      <>
        <Routes>
          {/* Público */}
          <Route path="/login" element={<Login />} />
          {/* Enlaces de los mails de Firebase Auth (restablecer contraseña) */}
          <Route path="/auth/action" element={<AuthAction />} />
          {/* 🔹 pública, fuera de Protected/Admin */}
          <Route path="/pwa-reset" element={<PwaReset />} />
          <Route path="/auth-debug" element={<AuthDebug />} />
          {/* Bloque protegido por sesión */}
          <Route element={<ProtectedRoute />}>
            {/* App del usuario */}
            <Route path="/app" element={<AppLayout />}>
              <Route index element={<AppHome />} />
              <Route path="notifications" element={<NotificationsListApp />} />
              <Route path="classes" element={<AppClassesList />} />
              <Route path="classes/:id" element={<ClassesDetail />} />
              <Route path="routines" element={<MyRoutines />} />
              <Route path="routines/:id" element={<RoutineDetail />} />
              <Route path="achievements" element={<AchievementsPage />} />
            </Route>

            <Route path="/app/profile" element={<Profile />} />
            <Route path="/app/bookings" element={<MyBookings />} />

            {/* Bloque admin */}
            <Route element={<AdminRoute />}>
              <Route path="/admin" element={<AdminLayout />}>
                <Route index element={<AdminDashboard />} />

                <Route path="classes" element={<AdminClassesList />} />
                <Route path="classes/new" element={<NewSession />} />
                <Route path="classes/:id/edit" element={<EditSession />} />
                <Route path="classes/bulk" element={<BulkSchedule />} />
                <Route path="classes/:id" element={<SessionDetail />} />
                <Route path="classes/:id/attendance" element={<SessionAttendance />} />
                <Route path="classes/types" element={<ClassTypesList />} />
                <Route path="classes/new-type" element={<ClassTypeNew />} />
                <Route path="classes/types/:id/edit" element={<ClassTypeEdit />} />

                {/* Usuarios */}
                <Route path="users" element={<UsersIndex />} />
                <Route path="users/assign" element={<AssignRole />} />
                <Route path="users/:id" element={<UserDetail />} />
                <Route path="users/:id/bulk-book" element={<BulkBookForUser />} />

                {/* Reportes */}
                <Route path="reports" element={<ReportsHome />} />
                <Route path="reports/occupancy" element={<ReportsOccupancy />} />
                <Route path="reports/checkins" element={<ReportsCheckins />} />

                {/* Notificaciones */}
                <Route path="notifications" element={<ReminderPanel />} />
                <Route path="notifications/list" element={<NotificationsList />} />

                {/* Planes */}
                <Route path="plans" element={<PlansIndex />} />
                <Route path="plans/:id" element={<PlanEdit />} />

                {/* Herramientas */}
                <Route path="timer" element={<IntervalTimer />} />

                {/* Rutinas */}
                <Route path="routines" element={<RoutinesList />} />
                <Route path="routines/new" element={<RoutineForm />} />
                <Route path="routines/:id/edit" element={<RoutineForm />} />
                <Route path="routines/:id/assign" element={<AssignRoutine />} />
                <Route path="routines/report" element={<TrainerReport />} />
                <Route path="routines/exercises" element={<ExercisesList />} />
                <Route path="routines/exercises/new" element={<ExerciseForm />} />
                <Route path="routines/exercises/:id/edit" element={<ExerciseForm />} />
              </Route>
            </Route>
          </Route>

          {/* Aliases en español */}
          <Route path="/admin/clases/*" element={<Navigate to="/admin/classes" replace />} />
          <Route path="/app/clases/*" element={<Navigate to="/app/classes" replace />} />

          {/* Home -> redirige según sesión */}
          <Route path="/" element={<RedirectBySession />} />

          {/* 404 */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>

        {/* PWA: prompts globales */}
        <InstallPrompt />
        <UpdatePrompt />
        <IosInstallHint />
        <OfflineBanner />
      </>
    </Suspense>
  );
}

function RedirectBySession() {
  const { user } = useAuth();
  // Si el AuthProvider aún no resolvió (ej.: undefined), mostramos loader
  if (typeof user === "undefined") {
    return <FullScreenLoader message="Inicializando sesión…" solidBackdrop />;
  }
  return <Navigate to={user ? "/app" : "/login"} replace />;
}
